import React, { useEffect, useState } from "react";
import { Grid, Paper, LinearProgress } from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import { motion, AnimatePresence } from "framer-motion";
import "../styles/Gamification.scss";
import Xp50 from "../assets/png/50xp.png";
import Xp150 from "../assets/png/150xp.png";
import Xp300 from "../assets/png/300xp.png";
import Xp500 from "../assets/png/500xp.png";
import Xp750 from "../assets/png/750xp.png";
import Xp1000 from "../assets/png/1000xp.png";
import Xp1500 from "../assets/png/1500xp.png";
import Xp2000 from "../assets/png/2000xp.png";
import Xp2500 from "../assets/png/2500xp.png";
import Xp3000 from "../assets/png/3000xp.png";
import Xp4000 from "../assets/png/4000xp.png";
import Xp5000 from "../assets/png/5000xp.png";
import Xp6000 from "../assets/png/6000xp.png";
import Xp7500 from "../assets/png/7500xp.png";
import Xp10000 from "../assets/png/10000xp.png";

const xpAchievements = [
  {
    id: 1,
    name: "First Step",
    xpRequired: 50,
    image: Xp50,
    description: "Reach 50 XP",
  },
  {
    id: 2,
    name: "Learning Enthusiast",
    xpRequired: 150,
    image: Xp150,
    description: "Reach 150 XP",
  },
  {
    id: 3,
    name: "XP Explorer",
    xpRequired: 300,
    image: Xp300,
    description: "Reach 300 XP",
  },
  {
    id: 4,
    name: "XP Master",
    xpRequired: 500,
    image: Xp500,
    description: "Reach 500 XP",
  },
  {
    id: 5,
    name: "XP Legend",
    xpRequired: 750,
    image: Xp750,
    description: "Reach 750 XP",
  },
  {
    id: 6,
    name: "XP Champion",
    xpRequired: 1000,
    image: Xp1000,
    description: "Reach 1000 XP",
  },
  {
    id: 7,
    name: "XP Elite",
    xpRequired: 1500,
    image: Xp1500,
    description: "Reach 1500 XP",
  },
  {
    id: 8,
    name: "XP Supreme",
    xpRequired: 2000,
    image: Xp2000,
    description: "Reach 2000 XP",
  },
  {
    id: 9,
    name: "XP Sovereign",
    xpRequired: 2500,
    image: Xp2500,
    description: "Reach 2500 XP",
  },
  {
    id: 10,
    name: "XP Grandmaster",
    xpRequired: 3000,
    image: Xp3000,
    description: "Reach 3000 XP",
  },
  {
    id: 11,
    name: "XP Immortal",
    xpRequired: 4000,
    image: Xp4000,
    description: "Reach 4000 XP",
  },
  {
    id: 12,
    name: "XP Divine",
    xpRequired: 5000,
    image: Xp5000,
    description: "Reach 5000 XP",
  },
  {
    id: 13,
    name: "XP Transcendent",
    xpRequired: 6000,
    image: Xp6000,
    description: "Reach 6000 XP",
  },
  {
    id: 14,
    name: "XP Eternal",
    xpRequired: 7500,
    image: Xp7500,
    description: "Reach 7500 XP",
  },
  {
    id: 15,
    name: "XP Ultimate",
    xpRequired: 10000,
    image: Xp10000,
    description: "Reach 10000 XP",
  },
];

const Gamification = ({ stats, badges, type }) => {
  const [userData, setUserData] = useState({
    total_coins: 0,
    total_xp: 0,
    streak_count: 0,
    streak_coins: 0,
    name: "",
    badge_name: "",
    level: 0,
    next_badge_name: null,
    next_level_xp: null,
    login_coins: 0,
    login_xp: 0,
    streak_xp: 0,
  });

  const [apiBadges, setApiBadges] = useState([]);

  useEffect(() => {
    const statsSource =
      stats ||
      (badges && !Array.isArray(badges) && !badges.data ? badges : null);

    if (statsSource) {
      setUserData({
        total_coins: statsSource.total_coins ?? 0,
        total_xp: statsSource.total_xp ?? 0,
        streak_count: statsSource.streak_count ?? 0,
        streak_coins: statsSource.streak_coins ?? 0,
        name: statsSource.name || "",
        badge_name: statsSource.badge_name || "",
        level: statsSource.level || 0,
        next_badge_name: statsSource.next_badge_name || null,
        next_level_xp: statsSource.next_level_xp || null,
        login_coins: statsSource.login_coins ?? 0,
        login_xp: statsSource.login_xp ?? 0,
        streak_xp: statsSource.streak_xp ?? 0,
      });
    }
  }, [stats, badges]);

  useEffect(() => {
    if (badges) {
      if (Array.isArray(badges)) {
        setApiBadges(badges);
      } else if (badges.data && Array.isArray(badges.data)) {
        setApiBadges(badges.data);
      } else {
        setApiBadges([]);
      }
    }
  }, [badges]);

  const level = userData.level;

  let currentLevelProgress = 0;
  if (userData.next_level_xp) {
    currentLevelProgress = (userData.total_xp / userData.next_level_xp) * 100;
  } else if (userData.level > 1) {
    currentLevelProgress = 100;
  } else {
    currentLevelProgress = ((userData.total_coins % 1000) / 1000) * 100;
  }

  const xpBadges = xpAchievements.map((achievement) => {
    const apiBadge = Array.isArray(apiBadges)
      ? apiBadges.find(
          (b) => b.id === achievement.id || b.name === achievement.name,
        )
      : null;
    return {
      ...achievement,
      icon: (
        <img
          src={achievement.image}
          alt={achievement.name}
          className="achievement-icon"
        />
      ),
      unlocked: apiBadge
        ? apiBadge.unlocked
        : userData.level >= achievement.id ||
          userData.total_xp >= achievement.xpRequired,
    };
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    },
  };

  return (
    <motion.div
      className="gamification-content"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {(type === "stats" || !type) && (
        <div className="stats-container">
          <motion.div
            variants={itemVariants}
            className="stat-card-wrapper coins-card-wrapper"
          >
            <Paper elevation={3} className="stat-card coins-card">
              <motion.div
                animate={{
                  rotateY: [0, 360],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatDelay: 5,
                }}
              >
                <div className="stat-icon-container coins-icon">
                  <MonetizationOnIcon className="coins-icon" />
                </div>
              </motion.div>
              <div>
                <div className="stat-value-container">
                  <div className="stat-value coins-value">
                    {userData.total_coins}
                  </div>
                  {/* {userData.login_coins + userData.streak_coins > 0 && (
                    <div className="daily-gain coins-gain">
                      +{userData.login_coins + userData.streak_coins} today
                    </div>
                  )} */}
                </div>
                <div className="stat-label coins-label">Total Credits</div>
              </div>
            </Paper>
          </motion.div>

          <motion.div
            variants={itemVariants}
            className="stat-card-wrapper xp-card-wrapper"
          >
            <Paper elevation={3} className="stat-card xp-card">
              <motion.div
                animate={{
                  rotate: [0, 10, -10, 0],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  repeatType: "reverse",
                }}
              >
                <div className="stat-icon-container xp-icon">
                  <EmojiEventsIcon className="xp-icon" />
                </div>
              </motion.div>
              <div>
                <div className="stat-value-container">
                  <div className="stat-value xp-value">{userData.total_xp}</div>
                  {/* {userData.login_xp + userData.streak_xp > 0 && (
                    <div className="daily-gain xp-gain">
                      +{userData.login_xp + userData.streak_xp} today
                    </div>
                  )} */}
                </div>
                <div className="stat-label xp-label">Total XP</div>
              </div>
            </Paper>
          </motion.div>

          <motion.div
            variants={itemVariants}
            className="stat-card-wrapper streak-card-wrapper"
          >
            <Paper elevation={3} className="stat-card streak-card">
              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                  filter: [
                    "drop-shadow(0 0 2px #FF5722)",
                    "drop-shadow(0 0 8px #FF5722)",
                    "drop-shadow(0 0 2px #FF5722)",
                  ],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <div className="stat-icon-container streak-icon">
                  <LocalFireDepartmentIcon className="streak-icon" />
                </div>
              </motion.div>
              <div>
                <div className="streak-info">
                  <div className="stat-value streak-value">
                    {userData.streak_count}
                  </div>
                  <div className="streak-unit">Days</div>
                </div>
                <div className="stat-label streak-label">Learning Streak!</div>
              </div>
            </Paper>
          </motion.div>

          <motion.div
            variants={itemVariants}
            className="stat-card-wrapper level-card-wrapper"
          >
            <Paper elevation={3} className="stat-card level-card">
              <div className="level-container">
                <div className="level-header">
                  <div className="level-info">
                    <div className="level-title">Level {level}</div>
                    <div className="level-type">{userData.badge_name}</div>
                  </div>
                  <div className="level-percent">
                    {Math.round(currentLevelProgress)}%
                  </div>
                </div>
                <LinearProgress
                  variant="determinate"
                  value={currentLevelProgress}
                  className="level-progress"
                />
                <div className="level-next">
                  {userData.next_level_xp
                    ? `Earn ${userData.next_level_xp - userData.total_xp} more XP to reach ${userData.next_badge_name || `Level ${level + 1}`}`
                    : "You have reached the maximum level!"}
                </div>
              </div>
            </Paper>
          </motion.div>
        </div>
      )}

      {(type === "badges" || !type) && (
        <div className="achievements-section">
          <div className="achievements-header">
            <h4 className="achievements-header-text">
              Experience Achievements
              <div className="achievements-counter">
                {xpBadges.filter((b) => b.unlocked).length} / {xpBadges.length}
              </div>
            </h4>
          </div>

          <Grid container spacing={4} className="achievement-grid">
            <AnimatePresence>
              {xpBadges.map((badge, index) => (
                <Grid item xs={12} sm={6} md={4} key={badge.id}>
                  <motion.div
                    variants={itemVariants}
                    whileHover={{ y: -8 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <div className="achievement-card-wrapper">
                      <Paper
                        elevation={1}
                        className={`achievement-card ${
                          badge.unlocked ? "unlocked" : "locked"
                        }`}
                      >
                        {badge.unlocked && (
                          <div className="achievement-unlocked-badge">
                            <WorkspacePremiumIcon />
                          </div>
                        )}
                        <div
                          className={`achievement-icon-container ${
                            badge.unlocked ? "unlocked" : "locked"
                          }`}
                        >
                          {badge.icon}
                        </div>
                        <div
                          className={`achievement-name ${
                            badge.unlocked ? "unlocked" : "locked"
                          }`}
                        >
                          {badge.name}
                        </div>
                      </Paper>
                    </div>
                  </motion.div>
                </Grid>
              ))}
            </AnimatePresence>
          </Grid>
        </div>
      )}
    </motion.div>
  );
};

export default Gamification;
